// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from "vue";

import UiTemplateComponent from "@lovetoknow/ui-template-component";
import UiTableOfContents from "@lovetoknow/ui-table-of-contents";
import UiArticleBody from "@lovetoknow/ui-article-body";
import ZebraLink from "@/components/zebra-link";

import App from "@/components/app";
import router from "@/router";
import store from "@/store";
import vueCookies from "vue-cookies";
import vueAnalytics from "vue-analytics";
import { TRACKER_SETTINGS, TRACKER_DATA, STAGE } from "@/common/consts";
import SweetModal from "sweet-modal-vue/src/plugin.js";
import i18n from "@/common/multilanguage/i18n";
import VueGtm from "@gtm-support/vue2-gtm";

import "./mixins";
import {
	initAnalytics,
	trackCLS,
	trackFID,
	trackLCP,
	trackFCP,
	auditOT,
} from "@lovetoknow/ltk-analytics";

import {
	getBucketId,
	getBucketConfig,
	readBucketConfigFromLocalStorage,
	getBucketConfigOverrides,
} from "@/common/feature-toggling";

import nodeConfig from "../package.json";

Vue.use(vueCookies);
Vue.use(SweetModal);

// Register global vue components
Object.entries({
	UiTemplateComponent,
	UiTableOfContents,
	UiArticleBody,
	ZebraLink,
}).forEach(([name, component]) => {
	Vue.component(name, component);
});

if (
	typeof window.__PRERENDER_INJECTED === "undefined" &&
	typeof window.__TEST__ == "undefined"
) {
	const endpoints = {
		meta_url:
			"https://us-central1-custom-site-analytics.cloudfunctions.net/router/metaInfo",
		page_exp_url:
			"https://us-central1-custom-site-analytics.cloudfunctions.net/router/pageExp",
		page_evt_url:
			"https://us-central1-custom-site-analytics.cloudfunctions.net/router/pageEvt",
		custom_evt_url:
			"https://us-central1-custom-site-analytics.cloudfunctions.net/router/customEvt",
	};
	const analytics = initAnalytics({
		appName: "wfw",
		appEnvironment: STAGE,
		expTrack: true,
		pageTrack: true,
		adTrack: true,
		version: nodeConfig.version,
		debug:
			isFeatureEnabled("DebugMode") &&
			isFeatureEnabled("DebugPerformance"),
		options: {},
		...endpoints,
	});
	window.$ltkAnalytics = analytics;

	const sessionTrackerId = window?.queryParams?.SessionTrackerId || null;
	if (sessionTrackerId) {
		window.$ltkAnalytics.setOptions?.({
			gam: {
				SessionTrackerId: sessionTrackerId,
			},
		});

		window?.dataLayer?.push({
			SessionTrackerId: sessionTrackerId,
		});
	}
}

if (
	typeof window.__PRERENDER_INJECTED === "undefined" &&
	typeof window.__TEST__ == "undefined"
) {
	if (isFeatureEnabled("trackCoreVitals")) {
		trackCLS();
		trackFID();
		trackLCP();
		trackFCP();
	}
	auditOT();

	Vue.use(vueAnalytics, {
		id: () => {
			return Object.values(TRACKER_DATA);
		},
		customIdFields: TRACKER_SETTINGS,
		router,
		ignoreRoutes: ["/dynamic-page/"],
		// autoTracking: {
		//     pageviewOnLoad: true
		// },
		ready() {
			Vue.$ga.set("dimension1", window.appVersion);
		},
		debug: {
			enabled:
				isFeatureEnabled("DebugMode") &&
				isFeatureEnabled("DebugAnalytics"),
		},
	});
}

if (
	typeof window.__PRERENDER_INJECTED === "undefined" &&
	typeof window.__TEST__ === "undefined"
) {
	(async function() {
		// Before mounting GTM, get the TBL bucket id or generate a new one
		const tblResponse = await connectToTBL();

		if (tblResponse) {
			store.commit("SET_FEATURE_TOGGLE_BUCKET_CONFIG", tblResponse);

			const dimensionStr = `${
				tblResponse.toggles ? `${getBucketId()}:` : ""
			}${tblResponse.toggles.join(",")}`;
			// Push the bucket ID to the datalayer in order for the GTM pageview events to have access to it
			dataLayer.push({ tbl_toggles: dimensionStr });

			if (window) {
				window.__TBL_LOADED = true;
				window.dispatchEvent(new Event("tbl_loaded"));
			}
		}

		Vue.use(VueGtm, {
			id: "GTM-5TPB9NP",
			debug: isFeatureEnabled("DebugMode"),
			vueRouter: router,
		});

		/**
		 * @note
		 * Although we are firing the "libload-aditude" custom event here,
		 * this is for future purposes. For now we are loading the Aditude
		 * script tag via the local libLoader library instead of GTM, as
		 * the Aditude team has requested we don't use GTM.
		 */
		if (!isFeatureEnabled("DisableThirdParty")) {
			timerPromise.then(() => {
				dataLayer.push({ event: "libload-aditude" });
				libloaderPromise.then(() => {
					libLoader.loadAditude();
				});
			});
		}
	})();
}

function connectToTBL() {
	const currentConfig = readBucketConfigFromLocalStorage();
	if (currentConfig !== null) {
		store.commit("SET_FEATURE_TOGGLE_BUCKET_CONFIG", currentConfig);
	}

	store.commit(
		"SET_FEATURE_TOGGLE_BUCKET_OVERRIDES",
		getBucketConfigOverrides()
	);
	return getBucketConfig()
		.then((config) => {
			return config;
		})
		.catch(({ message, data }) => {
			console.error(message, data);
		});
}

document.addEventListener("DOMContentLoaded", function() {
	/* eslint-disable no-new */
	new Vue({
		el: "#app",
		router,
		store,
		i18n,
		components: { App },
		template: "<App/>",
	});
});
